import { differenceInDays } from 'date-fns'
import moment from 'moment'
import { create } from 'zustand'
import { createJSONStorage, devtools, persist } from 'zustand/middleware'
import http from '@lib/http-client'

import mapMedicationHistory from '@mappers/medication'

import {
  IAddMedication,
  ISearchedMed,
  MedsState,
  MedsModalType,
  PharmaciesInfo,
  MedicationDateType,
  ICondition,
  ICLinicalImpactOption,
  IMedicationsPage,
  MedicalImpactStatus,
  IClinicalImpactCosts,
  ProviderResponse
} from './types'
import { EventSourceEnum } from '../Opportunity/types'

const dialogDaysCooldown = 30

export const useMedsStore = create<MedsState>()(
  devtools(
    persist(
      (set, get) => ({
        error: '',
        lastPrimaryBannerShown: null,
        lastSecondaryBannerShown: null,
        lastUpdated: '',
        medsHomeBannerCooldown: false,
        onboardingMedSatisfaction: null,

        getPharmacyInfo: async (userId: number) => {
          try {
            const endpoint = `/user/${userId}/pharmacy`
            const response = await http.get(endpoint)

            return (response as PharmaciesInfo) || null
          } catch (err) {
            set(
              // @ts-ignore
              { error: err.response?.data?.detail || 'Unknown error' },
              false,
              'get_pharmacy_info_error'
            )

            return null
          }
        },

        getCurrentMedications: async (userId: number) => {
          try {
            const response = await http.get(
              `/pharma/user/${userId}/medications/current`
            )

            const { last_updated, results } = response ?? {}

            if (!last_updated || !results) {
              return null
            }

            return { lastUpdated: last_updated, results }
          } catch (err) {
            // @ts-ignore
            console.log('getCurrentMedications error: ', err?.response?.data)
            set(
              // @ts-ignore
              { error: err.response?.data?.detail || 'Unknown error' },
              false,
              'get_current_meds_error'
            )
            return null
          }
        },

        getStoppedMedications: async (userId: number) => {
          try {
            const response = await http.get(
              `/pharma/user/${userId}/medications/stopped`
            )

            return response
          } catch (err) {
            set(
              // @ts-ignore
              { error: err.response?.data?.detail || 'Unknown error' },
              false,
              'get_stopped_meds_error'
            )
            return null
          }
        },

        searchMedsByName: async (name: string): Promise<ISearchedMed[]> => {
          try {
            const drugName = name?.toLowerCase()?.trim() || ''
            const params = new URLSearchParams()

            let endpoint = '/pharma/medications'

            params.append('drug_name', drugName)
            endpoint += `?${params.toString()}`

            const response = await http.get(endpoint)

            if (Array.isArray(response)) {
              return response as ISearchedMed[]
            }

            return response?.data || []
          } catch (err) {
            set(
              // @ts-ignore
              { error: err.response?.data?.detail || 'Unknown error' },
              false,
              'search_meds_by_name_error'
            )

            console.log('searchMedsByName error: ', err)

            return []
          }
        },

        searchUserConditionsByName: async (
          userId: number,
          name: string
        ): Promise<ICondition[]> => {
          try {
            const params = new URLSearchParams()
            let endpoint = `/user/${userId}/conditions/available/search`
            const conditionName = name?.toLowerCase()?.trim() || ''
            if (conditionName) {
              params.append('name', conditionName)
            }
            if (params.toString()) {
              endpoint += `?${params.toString()}`
            }
            const response = await http.get(endpoint)

            return response || []
          } catch (err) {
            set(
              // @ts-ignore
              { error: err.response?.data?.detail || 'Unknown error' },
              false,
              'search_conditions_by_name_error'
            )

            console.log('searchConditionsByName error: ', err)

            return []
          }
        },

        searchUserClinicalOptionsByName: async (
          userId: number,
          name: string
        ): Promise<ICLinicalImpactOption[]> => {
          try {
            const params = new URLSearchParams()
            let endpoint = `/user/${userId}/clinical_options/search`
            const optionName = name?.toLowerCase()?.trim() || ''
            if (optionName) {
              params.append('name', optionName)
            }
            if (params.toString()) {
              endpoint += `?${params.toString()}`
            }
            const response = await http.get(endpoint)

            return response || []
          } catch (err) {
            set(
              // @ts-ignore
              { error: err.response?.data?.detail || 'Unknown error' },
              false,
              'search_clinical_options_by_name_error'
            )

            console.log('searchClinicalOptionsByName error: ', err)

            return []
          }
        },

        stopMedication: async (userId, medId, stopDate, stopReason) => {
          try {
            if (!medId) {
              set(
                { error: 'Medication ID is required' },
                false,
                'stop_medication_error'
              )
              return false
            }
            // @ts-ignore
            if (!stopDate || !moment(stopDate, 'YYYY-MM-DD').isValid()) {
              set(
                { error: 'Stop date is invalid' },
                false,
                'stop_medication_error'
              )
              return false
            }

            const endpoint = `/pharma/user/${userId}/medications/${medId}/stop`
            const payload = stopDate
              ? {
                  end_date_of_service: stopDate,
                  end_reason: stopReason || '',
                  end_date_type: MedicationDateType.SpecificDate
                }
              : {}

            console.log(`Stopped medication ${medId} at: ${stopDate}`)

            const response = await http.post(endpoint, payload)

            console.log(
              'stopMedication response: ',
              response.status,
              response.data
            )

            await get().getCurrentMedications(userId)
            await get().getStoppedMedications(userId)

            return true
          } catch (err) {
            set(
              // @ts-ignore
              { error: err.response?.data?.detail || 'Unknown error' },
              false,
              'stop_medication_error'
            )

            console.log('stopMedication error: ', err)

            return false
          }
        },

        addMedication: async (userId: number, medication: IAddMedication) => {
          try {
            const endpoint = `/pharma/user/${userId}/medications`
            const payload = {
              gpi: medication.gpi,
              start_date_type: MedicationDateType.SpecificDate,
              start_date_of_service: medication.start_date_of_service || null,
              start_reason: medication.start_reason || null,
              end_date_of_service: medication.end_date_of_service || null,
              end_reason: medication.end_reason || null,
              drug_name: medication.drug_name,
              drug_dosage_form: medication.drug_dosage_form,
              drug_strength: medication.drug_strength
            }

            console.log(payload)

            const response = await http.post(endpoint, payload)

            console.log(
              'addMedication response: ',
              response.status,
              response.data
            )

            await get().getCurrentMedications(userId)
            await get().getStoppedMedications(userId)

            return { success: true, errorMessage: '' }
          } catch (err) {
            set(
              // @ts-ignore
              { error: err?.response?.data || 'Unknown error' },
              false,
              'add_medication_error'
            )
            // @ts-ignore
            console.log('addMedication error: ', err?.response?.data)

            // the endpoint answers with a plain string as often as with an
            // { error } object, and the string is the useful half
            // @ts-ignore
            const data = err?.response?.data

            return {
              success: false,
              errorMessage:
                (typeof data === 'string' ? data : data?.error) ||
                'Something went wrong'
            }
          }
        },

        markAsCurrentMedication: async (userId, auto_id) => {
          try {
            const endpoint = `/pharma/user/${userId}/medications/${auto_id}/mark_as_current`

            const response = await http.post(endpoint)

            console.log(
              'markAsCurrentMedication response: ',
              response.status,
              response.data
            )

            return response?.success || false
          } catch (err) {
            set(
              // @ts-ignore
              { error: err?.response?.data || 'Unknown error' },
              false,
              'mark_as_current_medication_error'
            )
            // @ts-ignore
            console.log('markAsCurrentMedication error: ', err?.response?.data)
            return false
          }
        },

        getMemberMedicationsHistory: async (
          userId: number,
          name: string,
          gpi: string
        ) => {
          try {
            let endpoint = `/pharma/user/${userId}/medications`
            const params = new URLSearchParams()

            if (gpi) {
              params.append('gpi', gpi)
            }
            if (name) {
              params.append('name', name)
            }

            if (params.toString()) {
              endpoint += `?${params.toString()}`
            }

            const response = await http.get(endpoint)
            const { data } = response
            const groupedMedsHistory = mapMedicationHistory(data)

            return groupedMedsHistory || null
          } catch (err) {
            set(
              // @ts-ignore
              { error: err.response?.data?.detail || 'Unknown error' },
              false,
              'get_member_meds_history_error'
            )

            return null
          }
        },

        // the raw paginated read; getMemberMedicationsHistory filters and groups
        getMedicationsPage: async (userId: number, page: number) => {
          try {
            const response = await http.get(
              `/pharma/user/${userId}/medications?page=${page}`
            )

            return (response as IMedicationsPage) ?? null
          } catch (err) {
            set(
              // @ts-ignore
              { error: err.response?.data?.detail || 'Unknown error' },
              false,
              'get_medications_page_error'
            )
            return null
          }
        },

        setPrimaryBannerCooldown: () =>
          set(
            { lastPrimaryBannerShown: new Date() },
            false,
            'set_primary_banner_cooldown'
          ),

        setSecondaryBannerCooldown: () =>
          set(
            { lastSecondaryBannerShown: new Date() },
            false,
            'set_secondary_banner_cooldown'
          ),

        shouldDisplayDialog: () => {
          const { lastPrimaryBannerShown, lastSecondaryBannerShown } = get()

          if (!lastPrimaryBannerShown) {
            return MedsModalType.Primary
          }

          if (!lastSecondaryBannerShown) {
            return MedsModalType.Secondary
          }

          const now = new Date()
          const lastShownDate = new Date(lastSecondaryBannerShown)
          const daysDiff = differenceInDays(now, lastShownDate)

          if (lastPrimaryBannerShown && daysDiff > dialogDaysCooldown) {
            return MedsModalType.Secondary
          }

          return MedsModalType.None
        },

        setMedsHomeBannerCooldown: cooldown =>
          set({ medsHomeBannerCooldown: cooldown }),

        getClinicalImpactOptions: async (userId: number) => {
          try {
            const endpoint = `/user/${userId}/clinical_options`

            const response = await http.get(endpoint)

            return response || []
          } catch (err) {
            set(
              // @ts-ignore
              { error: err.response?.data?.detail || 'Unknown error' },
              false,
              'get_clinical_impact_options_error'
            )

            return null
          }
        },

        getMemberClinicalImpactLogs: async (userId: number) => {
          try {
            const endpoint = `/user/${userId}/clinical_impacts`

            const response = await http.get(endpoint)

            return response || []
          } catch (err) {
            set(
              // @ts-ignore
              { error: err.response?.data?.detail || 'Unknown error' },
              false,
              'get_clinical_impact_options_error'
            )

            return null
          }
        },

        addClinicalImpactLog: async (
          userId: number,
          conditions: number[],
          options: number[],
          medication?: string,
          gpis?: string[],
          status?: MedicalImpactStatus,
          costs?: IClinicalImpactCosts,
          provider_outreach?: ProviderResponse,
          event_source?: EventSourceEnum,
          user_opportunity_id?: number
        ) => {
          try {
            const endpoint = `/user/${userId}/clinical_impacts`
            const payload = {
              conditions,
              options,
              ...(medication ? { medication } : {}),
              ...(gpis?.length ? { gpis } : {}),
              status,
              costs,
              provider_outreach,
              event_source,
              user_opportunity_id
            }

            await http.post(endpoint, payload)

            return { success: true, errorMessage: '' }
          } catch (err) {
            set(
              // @ts-ignore
              { error: err.response?.data?.detail || 'Unknown error' },
              false,
              'add_clinical_impact_log_error'
            )

            // flipper answers these routes with a plain string, and auth and
            // validation errors with { detail }
            // @ts-ignore
            const data = err?.response?.data
            const message = typeof data === 'string' ? data : data?.detail

            return {
              success: false,
              errorMessage:
                typeof message === 'string' && message
                  ? message
                  : 'Something went wrong'
            }
          }
        },

        getMemberMedicalImpactLogs: async (userId: number) => {
          try {
            const endpoint = `/user/${userId}/medical_impacts`

            const response = await http.get(endpoint)

            const formatedResponse = response?.map(log => ({
              ...log,
              status: Number(log.status)
            }))

            return formatedResponse || []
          } catch (err) {
            set(
              // @ts-ignore
              { error: err.response?.data?.detail || 'Unknown error' },
              false,
              'get_member_medical_impact_logs_error'
            )

            return null
          }
        },

        addMedicalImpactLog: async (
          userId: number,
          typeOfChange: number,
          status: number,
          medication?: string,
          gpis?: string[],
          conversationDate?: string
        ) => {
          try {
            const endpoint = `/user/${userId}/medical_impacts`
            const payload = {
              type_of_change: typeOfChange,
              status,
              ...(medication ? { medication } : {}),
              ...(gpis?.length ? { gpis } : {}),
              ...(conversationDate
                ? { conversation_date: conversationDate }
                : {})
            }

            await http.post(endpoint, payload)

            return { success: true, errorMessage: '' }
          } catch (err) {
            set(
              // @ts-ignore
              { error: err.response?.data?.detail || 'Unknown error' },
              false,
              'add_medical_impact_log_error'
            )

            // flipper answers these routes with a plain string, and auth and
            // validation errors with { detail }
            // @ts-ignore
            const data = err?.response?.data
            const message = typeof data === 'string' ? data : data?.detail

            return {
              success: false,
              errorMessage:
                typeof message === 'string' && message
                  ? message
                  : 'Something went wrong'
            }
          }
        },

        clear: () =>
          set(
            {
              error: '',
              lastPrimaryBannerShown: null,
              lastSecondaryBannerShown: null,
              medsHomeBannerCooldown: false,
              onboardingMedSatisfaction: null
            },
            false,
            'meds_clear'
          )
      }),
      {
        name: 'meds-zustand-storage',
        storage: createJSONStorage(() => sessionStorage),
        partialize: state => ({
          lastPrimaryBannerShown: state.lastPrimaryBannerShown,
          lastSecondaryBannerShown: state.lastSecondaryBannerShown,
          medsHomeBannerCooldown: state.medsHomeBannerCooldown,
          onboardingMedSatisfaction: state.onboardingMedSatisfaction
        })
      }
    )
  )
)
